import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Navigation from './components/Navigation';
import WhatsAppButton from './components/WhatsAppButton';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import CoursesPage from './pages/CoursesPage';
import ProtectedContent from './components/secure/ProtectedContent';
import TermsGate from './components/TermsGate';
import ErrorBoundary from './components/ErrorBoundary';
import ServiceNotice from './components/ServiceNotice';
import '@/index.css';


// Pages other than the first ones are downloaded only when someone opens them, so the site starts faster.
// After an update, an open tab may ask for a file that no longer exists: reload once to get the new version.
const lazyPage = (load) =>
  lazy(() =>
    load()
      .then((module) => {
        try { sessionStorage.removeItem('chunk-reloaded'); } catch (e) { /* ignore */ }
        return module;
      })
      .catch((error) => {
        let alreadyReloaded = true;
        try { alreadyReloaded = sessionStorage.getItem('chunk-reloaded') === '1'; sessionStorage.setItem('chunk-reloaded', '1'); } catch (e) { /* ignore */ }
        if (!alreadyReloaded) {
          window.location.reload();
          return new Promise(() => {});
        }
        throw error;
      })
  );

const PageLoading = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-slate-500" role="status">Loading…</div>
);

const RegisterPage = lazyPage(() => import('./pages/RegisterPage'));
const CourseDetailPage = lazyPage(() => import('./pages/CourseDetailPage'));
const StudentDashboard = lazyPage(() => import('./pages/StudentDashboard'));
const TeacherDashboard = lazyPage(() => import('./pages/TeacherDashboard'));
const AdminDashboard = lazyPage(() => import('./pages/AdminDashboard'));
const AboutPage = lazyPage(() => import('./pages/AboutPage'));
const PaymentSuccessPage = lazyPage(() => import('./pages/PaymentSuccessPage'));
const ForgotPasswordPage = lazyPage(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage'));
const QuizTracksPage = lazyPage(() => import('./pages/QuizTracksPage'));
const QuizPathPage = lazyPage(() => import('./pages/QuizPathPage'));
const QuizPlayerPage = lazyPage(() => import('./pages/QuizPlayerPage'));
const QuizReviewPage = lazyPage(() => import('./pages/QuizReviewPage'));
const CertificatesPage = lazyPage(() => import('./pages/CertificatesPage'));
const VerifyCertificatePage = lazyPage(() => import('./pages/VerifyCertificatePage'));
const LiveLecturesPage = lazyPage(() => import('./pages/LiveLecturesPage'));
const LectureDetailPage = lazyPage(() => import('./pages/LectureDetailPage'));
const BatchDetailPage = lazyPage(() => import('./pages/BatchDetailPage'));

// a crash on one page never takes down the rest; changing page clears it
const PageBoundary = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen">
          <ServiceNotice />
          <ErrorBoundary resetKey="nav"><Navigation /></ErrorBoundary>
          <PageBoundary>
          <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:courseId" element={<CourseDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/verify" element={<VerifyCertificatePage />} />
            <Route path="/verify/:code" element={<VerifyCertificatePage />} />
            <Route path="/certificates" element={<ProtectedRoute><CertificatesPage /></ProtectedRoute>} />

            <Route path="/quizzes" element={<ProtectedRoute><QuizTracksPage /></ProtectedRoute>} />
            <Route path="/quizzes/:courseId" element={<ProtectedRoute><QuizPathPage /></ProtectedRoute>} />
            <Route path="/quizzes/:courseId/take/:assessmentId" element={<ProtectedRoute><ProtectedContent reportTabHidden><QuizPlayerPage /></ProtectedContent></ProtectedRoute>} />
            <Route path="/quizzes/:courseId/attempt/:attemptId" element={<ProtectedRoute><ProtectedContent><QuizPlayerPage /></ProtectedContent></ProtectedRoute>} />
            <Route path="/live" element={<ProtectedRoute><LiveLecturesPage /></ProtectedRoute>} />
            <Route path="/live/:lectureId" element={<ProtectedRoute><ProtectedContent><LectureDetailPage /></ProtectedContent></ProtectedRoute>} />
            <Route path="/batches/:batchId" element={<ProtectedRoute roles={['admin', 'teacher']}><BatchDetailPage /></ProtectedRoute>} />
            <Route path="/quizzes/:courseId/review" element={<ProtectedRoute><QuizReviewPage /></ProtectedRoute>} />
            
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute roles={['student']}>
                  <StudentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher"
              element={
                <ProtectedRoute roles={['teacher']}>
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
          </PageBoundary>
          <WhatsAppButton />
          <TermsGate />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;